/**
 * Utility functions for formatting communication-related data
 */

/**
 * Format email date in a human-readable format
 * - Shows "X min ago" for times less than an hour
 * - Shows "X hours ago" for times less than a day
 * - Shows "Yesterday" for yesterday
 * - Shows "X days ago" for times less than a week
 * - Shows full date for older emails
 *
 * @param date - The date to format
 * @returns Formatted date string
 */
export function formatEmailDate(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) {
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours === 0) {
      const minutes = Math.floor(diff / (1000 * 60));
      return `${minutes} min ago`;
    }
    return `${hours} hours ago`;
  }
  if (days === 1) {
    return 'Yesterday';
  }
  if (days < 7) {
    return `${days} days ago`;
  }
  return date.toLocaleDateString();
}

/**
 * Format call duration from seconds to MM:SS format
 *
 * @param seconds - Duration in seconds
 * @returns Formatted duration string (e.g., "5:32")
 */
export function formatCallDuration(seconds: number): string {
  if (seconds === 0) return '0:00';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

/**
 * Calculate the number of SMS segments required for a message
 * - GSM-7 encoding (ASCII): Single segment up to 160 characters, 153 chars per multi-part segment
 * - UCS-2 encoding (Unicode): Single segment up to 70 characters, 67 chars per multi-part segment
 *
 * @param message - The SMS message text
 * @returns Number of segments required
 */
export function calculateSMSSegments(message: string): number {
  // Check if message contains Unicode characters (outside ASCII range)
  // biome-ignore lint/suspicious/noControlCharactersInRegex: Checking for non-ASCII characters
  const hasUnicode = /[^\x00-\x7F]/.test(message);

  const length = message.length;

  if (hasUnicode) {
    // UCS-2 encoding: 70 chars single, 67 chars multi-part
    if (length <= 70) return 1;
    return Math.ceil(length / 67);
  }

  // GSM-7 encoding: 160 chars single, 153 chars multi-part
  if (length <= 160) return 1;
  return Math.ceil(length / 153);
}

/**
 * Format SMS cost based on segment count and rate
 *
 * @param segments - Number of SMS segments
 * @param rate - Cost per segment (default: $0.01)
 * @returns Formatted cost string (e.g., "$0.03")
 */
export function formatSMSCost(segments: number, rate = 0.01): string {
  return `$${(segments * rate).toFixed(2)}`;
}

/**
 * Format tenant contact name by joining first and last name
 * Filters out empty strings and joins with a space
 *
 * @param contact - Contact object with first_name and last_name properties
 * @returns Formatted full name (e.g., "John Doe") or empty string if both names are empty
 */
export function formatTenantContactName(contact: {
  first_name: string;
  last_name: string;
}): string {
  const firstName = contact.first_name || '';
  const lastName = contact.last_name || '';
  return [firstName, lastName].filter(Boolean).join(' ');
}
